import { cn } from '@/lib/utils'

/**
 * Canonical marketing card surface. Brand rules baked in: sharp corners,
 * tokenized border/background, and NO decorative drop shadows — hover
 * feedback comes from border/background shifts only.
 *
 * Replaces the drifted copy-paste recipes (`bg-card border-border`,
 * `bg-muted/50 border-line-strong`, `bg-card/30 border-border/50`, ...).
 */
interface CardProps extends React.HTMLAttributes<HTMLElement> {
  variant?:
    | 'default'
    | 'muted'
    | 'outlined'
    | 'interactive'
    | 'highlighted'
    | 'grade'
    | 'accent'
  padding?: 'default' | 'compact' | 'none'
  as?: 'div' | 'article'
}

const VARIANTS: Record<NonNullable<CardProps['variant']>, string> = {
  default: 'bg-card border-line-strong',
  muted: 'bg-muted/50 border-line-strong',
  outlined: 'bg-transparent border-line-strong',
  interactive:
    'bg-card border-line-strong transition-colors duration-300 hover:border-primary/50',
  highlighted: 'border-primary bg-primary/5',
  // Signature treatment: default surface under a 2px grade-gradient rule
  // pinned to the top edge. The rule is decorative only — it never encodes a
  // score, so it stays aria-hidden and carries no non-color meaning.
  grade: 'relative bg-card border-line-strong',
  // Pink boundary for the "recommended / most popular" card in a set. Unlike
  // `highlighted` (blue, tinted fill) this leaves the surface unchanged.
  accent: 'bg-card border-cta',
}

const PADDINGS: Record<NonNullable<CardProps['padding']>, string> = {
  default: 'p-6',
  compact: 'p-4',
  none: '',
}

export function Card({
  variant = 'default',
  padding = 'default',
  as: Tag = 'div',
  className,
  children,
  ...props
}: CardProps) {
  return (
    <Tag
      className={cn('border', VARIANTS[variant], PADDINGS[padding], className)}
      {...props}
    >
      {variant === 'grade' && (
        <span
          aria-hidden='true'
          className='pointer-events-none absolute inset-x-0 top-0 h-0.5'
          style={{ background: 'var(--pm-grade-gradient)' }}
        />
      )}
      {children}
    </Tag>
  )
}

export default Card
